import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { useAppDispatch, useAppSelector } from "@store/store";
import { PlanItem } from "@dto/PlanItem";
import { useTranslation } from "react-i18next";
import {
  selectPlan,
  selectPlanDayDateType,
  selectSelectedDayValue,
  setSelectedDayValue,
} from "@store/plan/planReducer";
import { selectPlanItems } from "@store/plan/planReducer";
import { TFunction } from "i18next";
import dayjs from "dayjs";
import { setOverlayScreenAction } from "@store/app/appReducer";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { ExternalIcon } from "@icons/ExternalIcon";

import "./DaySelector.css";
import { ScrollHorizontal } from "@ui-kit/ScrollHorizontal/ScrollHorizontal";

interface DaySelectorItem {
  value: number | "edit" | "all" | null;
  label: string;
  active?: boolean;
}

/**
 * Parse date string like "2024-04-20" to "4/20"
 */
const parseDate = (value: string, addDays: number = 0) => {
  return dayjs(value).add(addDays, "day").format("M/DD");
};

const getDaySelectorItems = (
  dayDuration: number | null | undefined,
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
  selectedValue: number | "all" | null,
  isEditable: boolean,
  planItems: PlanItem[],
  filterDaysWithoutItems: boolean,
  t: TFunction,
) => {
  if (!dayDuration) {
    return [];
  }

  const result = [] as DaySelectorItem[];

  if (isEditable) {
    if (startsOn && endsOn) {
      result.push({
        value: "edit",
        label: `${parseDate(startsOn)}-${parseDate(endsOn)}`,
      });
    } else {
      result.push({
        value: "edit",
        label: `${dayDuration} ${t("screens.plan.days") ?? ""}`,
      });
    }
  }

  result.push({
    value: "all",
    label: t("common.all") ?? "",
    active: selectedValue === "all",
  });

  for (let i = 1; i <= dayDuration; ++i) {
    const label = startsOn
      ? parseDate(startsOn, i - 1)
      : `${t("common.day") ?? ""} ${i}`;

    if (filterDaysWithoutItems) {
      const planItemOnDayIndex = planItems.findIndex(
        (planItem) => planItem.dayIndex === i,
      );
      if (planItemOnDayIndex === -1) {
        continue;
      }
    }

    result.push({
      value: i,
      label: label,
      active: selectedValue === i,
    });
  }

  if (
    !filterDaysWithoutItems ||
    planItems.findIndex((planItem) => planItem.dayIndex === null) > -1
  ) {
    result.push({
      value: null,
      label: t("common.unscheduled") ?? "",
      active: selectedValue === null,
    });
  }

  return result;
};

interface DaySelectorProps {
  /**
   * Whether the button to edit days is present.
   */
  isEditable?: boolean;
  /**
   * Whether to filter out days with no plan items.
   */
  filterDaysWithoutItems?: boolean;
}

const DaySelector: FunctionComponent<DaySelectorProps> = ({
  isEditable = true,
  filterDaysWithoutItems = false,
}) => {
  const plan = useAppSelector(selectPlan);
  const planItems = useAppSelector(selectPlanItems);
  const selectedDayValue = useAppSelector(selectSelectedDayValue);
  const dispatch = useAppDispatch();
  const startsOn = plan?.startsOn;
  const endsOn = plan?.endsOn;
  const dayDuration = plan?.dayDuration;
  const view = useAppSelector(selectPlanDayDateType);
  const { t } = useTranslation();

  const daySelectorItems = useMemo(() => {
    return getDaySelectorItems(
      dayDuration,
      startsOn,
      endsOn,
      selectedDayValue,
      isEditable,
      planItems,
      filterDaysWithoutItems,
      t,
    );
  }, [
    dayDuration,
    startsOn,
    endsOn,
    selectedDayValue,
    isEditable,
    planItems,
    filterDaysWithoutItems,
    t,
  ]);

  useEffect(() => {
    if (
      filterDaysWithoutItems &&
      daySelectorItems.findIndex((x) => x.value === selectedDayValue) < 0
    ) {
      // If filtering out days without items, set to "all" if there old day value has no items.
      dispatch(setSelectedDayValue("all"));
    }
  }, [filterDaysWithoutItems, daySelectorItems, selectedDayValue, dispatch]);

  const handleSelectDay = useCallback(
    (value: null | "all" | number | "edit") => () => {
      if (value === "edit") {
        dispatch(
          setOverlayScreenAction({
            name: "PlanDatePicker",
            params: { view: view },
          }),
        );
      } else {
        dispatch(setSelectedDayValue(value));
      }
    },
    [dispatch, view],
  );

  return (
    <ScrollHorizontal>
      <div className="day-selector-component">
        {daySelectorItems.map((item) => (
          <TabButton
            key={item.value}
            label={item.label}
            iconPrefix={
              item.value === "edit" ? (
                <ExternalIcon
                  size={20}
                  color={colors.neutral600}
                  svgName="calendar"
                />
              ) : undefined
            }
            variant="outline"
            active={item.active}
            onPress={handleSelectDay(item.value)}
          />
        ))}
      </div>
    </ScrollHorizontal>
  );
};

export { DaySelector };
